
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabase';

export const useHOManager = () => {
  const queryClient = useQueryClient();

  // Get pending products for margin fixation
  const pendingProducts = useQuery({
    queryKey: ['pendingProducts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          vendors(name, status)
        `)
        .eq('status', 'posted')
        .eq('margin_fixed', false);
      
      if (error) throw error;
      return data;
    }
  });

  // Get sales orders approved by branch managers for HO processing
  const salesOrders = useQuery({
    queryKey: ['hoSalesOrders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sales_orders')
        .select(`
          *,
          customers(name, phone, email),
          products(model_name, category, vendor_id, value_addition, making_charges),
          vendors(name)
        `)
        .eq('status', 'branch_approved')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  // Get stone rates
  const stoneRates = useQuery({
    queryKey: ['stoneRates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stone_rates')
        .select('*')
        .order('stone_type', { ascending: true });
      
      if (error) throw error;
      return data;
    }
  });

  // Update product margins
  const updateMargins = useMutation({
    mutationFn: async (marginData: any) => {
      const { data, error } = await supabase
        .from('margin_settings')
        .insert({
          ...marginData,
          approved_by: 'ho-manager-id',
          approved_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingProducts'] });
      queryClient.invalidateQueries({ queryKey: ['marginSettings'] });
    }
  });

  // Activate product after margin fixation
  const activateProduct = useMutation({
    mutationFn: async (productId: string) => {
      const { data, error } = await supabase
        .from('products')
        .update({ 
          status: 'active',
          margin_fixed: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', productId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingProducts'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    }
  });

  // Forward order to vendor (HOM function)
  const forwardToVendor = useMutation({
    mutationFn: async ({ orderId, vendorId, notes }: { 
      orderId: string; 
      vendorId: string;
      notes?: string;
    }) => {
      const { data, error } = await supabase
        .from('sales_orders')
        .update({ 
          status: 'forwarded_to_vendor',
          vendor_id: vendorId,
          ho_notes: notes,
          forwarded_at: new Date().toISOString(),
          forwarded_by: 'ho-manager-id'
        })
        .eq('id', orderId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hoSalesOrders'] });
    }
  });

  // Request vendor change (requires OH approval)
  const requestVendorChange = useMutation({
    mutationFn: async ({ orderId, newVendorId, reason }: { 
      orderId: string; 
      newVendorId: string;
      reason: string;
    }) => {
      const { data, error } = await supabase
        .from('sales_orders')
        .update({ 
          status: 'vendor_change_requested',
          requested_vendor_id: newVendorId,
          vendor_change_reason: reason,
          change_requested_at: new Date().toISOString(),
          change_requested_by: 'ho-manager-id'
        })
        .eq('id', orderId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hoSalesOrders'] });
    }
  });

  // Update gold rates
  const updateGoldRates = useMutation({
    mutationFn: async (rateData: any) => {
      const { data, error } = await supabase
        .from('gold_rates')
        .upsert({
          date: new Date().toISOString().split('T')[0],
          ...rateData,
          updated_by: 'ho-manager-id'
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goldRate'] });
    }
  });

  // Update stone rates
  const updateStoneRates = useMutation({
    mutationFn: async (stoneRateData: any) => {
      const { data, error } = await supabase
        .from('stone_rates')
        .upsert({
          ...stoneRateData,
          updated_by: 'ho-manager-id',
          updated_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stoneRates'] });
    }
  });

  return {
    pendingProducts,
    salesOrders,
    stoneRates,
    updateMargins,
    activateProduct,
    forwardToVendor,
    requestVendorChange,
    updateGoldRates,
    updateStoneRates
  };
};